/* DB Inspector — sidebar panel: table list, search, recent tables */

import { state, setPanelLoading, esc, escAttr } from './state.js';
import { api } from './api.js';
import { openTableTab } from './data-wrangler.js';

const RECENT_KEY = 'agentcpq_db_recent_tables';
const MAX_RECENT = 10;

let allTables = [];
let loaded = false;

// ---------------------------------------------------------------------------
// Recent tables (localStorage)
// ---------------------------------------------------------------------------

function _loadRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
  } catch { return []; }
}

function _saveRecent(list) {
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}

export function addRecentTable(name) {
  const recent = _loadRecent().filter(n => n !== name);
  recent.unshift(name);
  _saveRecent(recent);
  _renderRecent();
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function _renderRecent() {
  const container = document.getElementById('dbTablesRecent');
  if (!container) return;

  const recent = _loadRecent();
  if (recent.length === 0) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = `
    <div class="db-recent-section">
      <div class="db-recent-header">Recent</div>
      ${recent.map(name => `
        <div class="db-table-row db-table-recent" data-table="${escAttr(name)}">
          <span class="db-table-icon">\u{1F5C3}</span>
          <span class="db-table-name">${esc(name)}</span>
        </div>
      `).join('')}
    </div>
  `;

  container.querySelectorAll('.db-table-row').forEach(row => {
    row.addEventListener('click', () => {
      const tableName = row.dataset.table;
      addRecentTable(tableName);
      openTableTab(tableName);
    });
  });
}

function _renderTableList(tables) {
  const container = document.getElementById('dbTablesList');
  if (!container) return;

  if (tables.length === 0) {
    container.innerHTML = '<div class="db-tables-placeholder">No tables found</div>';
    return;
  }

  container.innerHTML = tables.map(name => `
    <div class="db-table-row" data-table="${escAttr(name)}">
      <span class="db-table-icon">\u{1F5C3}</span>
      <span class="db-table-name">${esc(name)}</span>
    </div>
  `).join('');

  container.querySelectorAll('.db-table-row').forEach(row => {
    row.addEventListener('click', () => {
      const tableName = row.dataset.table;
      addRecentTable(tableName);
      openTableTab(tableName);
    });
  });
}

// ---------------------------------------------------------------------------
// Search / filter
// ---------------------------------------------------------------------------

function _onSearchInput(e) {
  const query = (e.target.value || '').toLowerCase().trim();
  if (!query) {
    _renderTableList(allTables);
    return;
  }
  const filtered = allTables.filter(name => name.toLowerCase().includes(query));
  _renderTableList(filtered);
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

async function _loadTables() {
  const container = document.getElementById('dbTablesList');
  if (container) container.innerHTML = '<div class="db-tables-placeholder">Loading tables...</div>';

  setPanelLoading('db-inspector', true);
  try {
    const data = await api.dbTables();
    allTables = data.tables || [];
    _renderTableList(allTables);
    loaded = true;
  } catch (err) {
    if (container) container.innerHTML = `<div class="db-tables-placeholder db-error">Error: ${esc(err.message)}</div>`;
  } finally {
    setPanelLoading('db-inspector', false);
  }
}

// ---------------------------------------------------------------------------
// Init (called once on first panel show)
// ---------------------------------------------------------------------------

export function initDbInspector() {
  _renderRecent();

  const searchInput = document.getElementById('dbSearchInput');
  if (searchInput) {
    searchInput.addEventListener('input', _onSearchInput);
  }

  const btnRefresh = document.getElementById('btnRefreshTables');
  if (btnRefresh) {
    btnRefresh.addEventListener('click', () => _loadTables());
  }

  _loadTables();
}
